import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dog,
  Cat,
  Bird,
  Fish,
  Heart,
  Users,
  Shield,
  FileText,
  Calendar,
  MessageSquare,
  Award,
  Camera,
  Stethoscope,
  Vote,
  Sparkles,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const FeaturesPage = () => {
  const features = [
    {
      icon: Heart,
      title: "Multi-Pet Support",
      description: "Register ALL your pets - dogs, cats, birds, fish, and more! Use our full suite of tools for any pet type.",
      badge: "New",
      badgeColor: "bg-green-100 text-green-800",
      highlight: true
    },
    {
      icon: FileText,
      title: "Complete Health Records",
      description: "Track vaccinations, medications, vet visits, and medical history. Never lose important health information again.",
      badge: null
    },
    {
      icon: Users,
      title: "Care Team Management",
      description: "Add vets, groomers, trainers, and pet sitters to your pet's care team. Share access with family members.",
      badge: null
    },
    {
      icon: Calendar,
      title: "Events & Meetups",
      description: "Discover local pet events, training workshops, and community meetups. Connect with pet lovers near you.",
      badge: null
    },
    {
      icon: MessageSquare,
      title: "Community Forums",
      description: "Ask questions, share tips, and learn from experienced pet owners in our vibrant community forums.",
      badge: null
    },
    {
      icon: Award,
      title: "Pet Rankings & Recognition",
      description: "Showcase your pets in community rankings. Vote for featured pets and celebrate our furry friends.",
      badge: null
    },
    {
      icon: Camera,
      title: "Photo Albums & Galleries",
      description: "Create beautiful photo albums for your pets. Share memories with friends and family.",
      badge: null
    },
    {
      icon: Stethoscope,
      title: "Health Detection Tools",
      description: "AI-powered tools to help identify potential health concerns. Get guidance on when to see a vet.",
      badge: "Beta"
    },
    {
      icon: Vote,
      title: "Request New Clubs",
      description: "Vote for new pet-type clubs! Help us build communities for cats, birds, fish, and more.",
      badge: "New",
      badgeColor: "bg-green-100 text-green-800"
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Your data is never sold. We're a nonprofit focused on pet welfare, not profits.",
      badge: null
    }
  ];

  const comingSoon = [
    "Dual club membership discounts",
    "AI Help Agent for pet questions",
    "Email notifications & reminders",
    "Genetic health tracking",
    "Pet ancestry trees",
    "Marketplace for pet supplies"
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-4">
          <Sparkles className="w-4 h-4" />
          Platform Features
        </div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
          Features & Benefits
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
          Everything you need to care for your pets and connect with fellow pet lovers.
          And we're just getting started!
        </p>
      </div>

      {/* Multi-Pet Highlight */}
      <Card className="mb-12 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex gap-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Dog className="w-6 h-6 text-primary" />
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Cat className="w-6 h-6 text-purple-600" />
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Bird className="w-6 h-6 text-green-600" />
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Fish className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <Badge className="bg-green-100 text-green-800 mb-2">New Feature</Badge>
              <h2 className="text-2xl font-bold mb-2">Now Supporting All Pet Types!</h2>
              <p className="text-muted-foreground mb-4">
                While we're the All American Dog Club, we know many of you have other beloved pets too.
                Now you can register and track ALL your pets - cats, birds, fish, and more - using our complete toolkit.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <Link to="/my-pets">
                  <Button className="gap-2">
                    Add Your Pets
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/request-club">
                  <Button variant="outline" className="gap-2">
                    Request a Club
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card 
              key={`feature-${feature.title}`} 
              className={`transition-all hover:shadow-md ${feature.highlight ? 'ring-2 ring-primary/20' : ''}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{feature.title}</h3>
                      {feature.badge && (
                        <Badge className={feature.badgeColor || "bg-blue-100 text-blue-800"}>
                          {feature.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Coming Soon */}
      <Card className="bg-muted/50">
        <CardContent className="p-8">
          <h2 className="text-xl font-bold mb-4 text-center">Coming Soon</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {comingSoon.map((item) => (
              <div key={`coming-${item}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary/50" />
                {item}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Have a feature request? <Link to="/forums" className="text-primary hover:underline">Let us know in the forums!</Link>
          </p>
        </CardContent>
      </Card>

      {/* CTA */}
      <div className="text-center mt-12">
        <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
        <p className="text-muted-foreground mb-6">Join thousands of pet lovers in our community.</p>
        <div className="flex gap-4 justify-center">
          <Link to="/register">
            <Button size="lg" className="gap-2">
              Join Now
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/about">
            <Button size="lg" variant="outline">
              Learn More
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;
